// Role constants matching backend UserRole enum
export const ROLES = {
  SUPER_ADMIN: 'superAdmin',
  OWNER: 'owner',
  ADMIN: 'admin',
  STAFF: 'staff',
};

// Role hierarchy for comparison (higher number = more permissions)
export const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 4,
  [ROLES.OWNER]: 3,
  [ROLES.ADMIN]: 2,
  [ROLES.STAFF]: 1,
};

/**
 * Check if user has a specific role
 */
export const isRole = (userRole, targetRole) => {
  return userRole === targetRole;
};

/**
 * Check if user is Super Admin
 */
export const isSuperAdmin = (userRole) => {
  return userRole === ROLES.SUPER_ADMIN;
};

/**
 * Check if user is Organization Owner
 */
export const isOwner = (userRole) => {
  return userRole === ROLES.OWNER;
};

/**
 * Check if user is Admin
 */
export const isAdmin = (userRole) => {
  return userRole === ROLES.ADMIN;
};

/**
 * Check if user is Staff
 */
export const isStaff = (userRole) => {
  return userRole === ROLES.STAFF;
};

/**
 * Check if user has admin-level permissions (Owner or Admin)
 */
export const hasAdminPermissions = (userRole) => {
  return isOwner(userRole) || isAdmin(userRole);
};

/**
 * Check if user has management permissions (Owner, Admin, or Super Admin)
 */
export const hasManagementPermissions = (userRole) => {
  return isSuperAdmin(userRole) || isOwner(userRole) || isAdmin(userRole);
};

/**
 * Check if user can manage departments (Owner or Admin only)
 */
export const canManageDepartments = (userRole) => {
  return isOwner(userRole) || isAdmin(userRole);
};

/**
 * Check if user can manage users (Owner or Admin only)
 */
export const canManageUsers = (userRole) => {
  return isOwner(userRole) || isAdmin(userRole);
};

/**
 * Check if user can view organization data (Owner, Admin, or Staff)
 */
export const canViewOrgData = (userRole) => {
  return isOwner(userRole) || isAdmin(userRole) || isStaff(userRole);
};

/**
 * Check if user can create organizations (Super Admin only)
 */
export const canCreateOrganizations = (userRole) => {
  return isSuperAdmin(userRole);
};

/**
 * Check if user can access organization features (not Super Admin)
 */
export const canAccessOrgFeatures = (userRole) => {
  return !isSuperAdmin(userRole);
};

/**
 * Check if user has higher or equal role level
 */
export const hasRoleLevel = (userRole, minimumRole) => {
  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const minimumLevel = ROLE_HIERARCHY[minimumRole] || 0;
  return userLevel >= minimumLevel;
};

/**
 * Check if user can perform action based on multiple role requirements
 */
export const canPerformAction = (userRole, allowedRoles = []) => {
  if (allowedRoles.length === 0) return true;
  return allowedRoles.includes(userRole);
};

/**
 * Get user role display name
 */
export const getRoleDisplayName = (userRole) => {
  const roleNames = {
    [ROLES.SUPER_ADMIN]: 'Super Administrator',
    [ROLES.OWNER]: 'Organization Owner',
    [ROLES.ADMIN]: 'Administrator',
    [ROLES.STAFF]: 'Staff Member',
  };
  return roleNames[userRole] || 'Unknown Role';
};

/**
 * Get available actions for user role
 */
export const getAvailableActions = (userRole) => {
  const actions = {
    [ROLES.SUPER_ADMIN]: [
      'manage_system',
      'create_organizations',
      'manage_all_users',
      'view_system_logs',
    ],
    [ROLES.OWNER]: [
      'manage_organization',
      'manage_departments',
      'manage_users',
      'view_all_data',
      'manage_settings',
    ],
    [ROLES.ADMIN]: [
      'manage_departments',
      'manage_users',
      'view_all_data',
      'manage_content',
    ],
    [ROLES.STAFF]: [
      'view_data',
      'create_content',
      'view_documents',
      'use_chat',
    ],
  };
  return actions[userRole] || [];
};

/**
 * Check if user can perform specific action
 */
export const canPerformSpecificAction = (userRole, action) => {
  const availableActions = getAvailableActions(userRole);
  return availableActions.includes(action);
};

/**
 * Role-based component wrapper utility
 */
export const withRoleCheck = (
  Component,
  allowedRoles = [],
  fallback = null,
) => {
  return (props) => {
    const userRole = props.userRole || props.user?.role;

    if (!userRole) {
      return fallback;
    }

    const hasAccess = canPerformAction(userRole, allowedRoles);
    return hasAccess ? <Component {...props} /> : fallback;
  };
};
